import { useEffect, useLayoutEffect, useRef } from 'react';
import { useIntl } from 'react-intl';
import { Outlet, useLocation } from 'react-router-dom';
import { useMediaQuery } from '@uidotdev/usehooks';
import { LazyMotion, m, MotionConfig } from 'motion/react';

import { CreateDialogHost } from 'components/create-dialog/CreateDialogHost';
import ExportRibbon from 'components/export-ribbon/ExportRibbon';
import { GlobalSearchDialog } from 'components/global-search/GlobalSearchDialog';
import { LinkPeekHost } from 'components/global-search/LinkPeekHost';
import { ListHeaderSlotsProvider } from 'components/tables/list-table/filter-bar/ListHeaderSlots';
import OnboardingAssistant from 'components/OnboardingAssistant';
import PageTitle from 'components/PageTitle';
import { useIsMobile } from 'hooks/useIsMobile';
import SubDrawer from 'layout/MainLayout/drawers/SubDrawer';
import TemporaryDrawer from 'layout/MainLayout/drawers/TemporaryDrawer';
import { MAIN_CONTENT_AREA_ID } from 'layout/MainLayout/useContentAreaRect';
import { AiChatSessionProvider } from 'pages/ai-chat/AiChatSessionProvider';
import FloatingAiAssistant from 'pages/ai-chat/floating/FloatingAiAssistant';
import { useDispatch } from 'store';
import { openDrawer } from 'store/reducers/menu';
import { useTour } from 'tour/TourProvider';
import { cn } from 'utils/cn';
import { readSidebarExpanded } from 'utils/sidebarExpanded';
import { FOCUS_RING } from './focusRing';
import Inbox from './Inbox';
import { PanelBoxProperties, usePanelBox } from './panelBox';
import Sidebar from './Sidebar';
import TopBar from './TopBar';
import { useOnePanelOpen } from './useOnePanelOpen';
import { usePageChangeFocus } from './usePageChangeFocus';

/** Motion's features, layout animations included, for the `m` elements of the frame: fetched once it mounts. */
const motionFeatures = () => import('./motionFeatures').then((module) => module.default);

/** From here up the sidebar opens expanded, below it as the rail; classic waits for `DRAWER_OPEN_QUERY`. */
const SIDEBAR_OPEN_QUERY = '(min-width: 1280px)';

type Props = {
    focusedEditMode: boolean;
    isFullscreen: boolean;
    /** A timecard form is the page: the AI panel shows beside it rather than over its answer box. */
    isTimecardForm?: boolean;
};

// The two drawers as the one detached box. Each measures its width itself, so a resize of the content area,
// as the sidebar collapses, renders the drawer again and not the whole layout.
function DetachedSubDrawer() {
    return <SubDrawer detached={usePanelBox()} />;
}

function DetachedTemporaryDrawer() {
    return <TemporaryDrawer detached={usePanelBox()} />;
}

/**
 * The new design's frame: its sidebar beside the top bar and the page on the grey canvas the cards sit on,
 * the Timecard center and the Inbox in one detached box, the AI panel without its floating launcher and the
 * ⌘K dialog. Every side panel starts under the top bar, and of the Timecard center, the Inbox, the AI panel
 * and the panels a page joins to the rule, one is open at a time. It has no page footer. Everything in it can start a creation in the create dialog,
 * which it hosts. Its first Tab stop is Skip to content, which moves focus past the sidebar to `<main>`; a page
 * change moves focus to the new page's `<h1>`.
 */
function NewDesignLayout({ focusedEditMode, isFullscreen, isTimecardForm = false }: Props) {
    const intl = useIntl();
    const dispatch = useDispatch();
    const isMobile = useIsMobile();
    const { pathname } = useLocation();
    const opensExpanded = useMediaQuery(SIDEBAR_OPEN_QUERY);
    const { active: touring } = useTour();
    const mainRef = useRef<HTMLElement>(null);
    useOnePanelOpen();
    usePageChangeFocus(mainRef);

    // The store starts at classic's width; this follows the viewport across the new design's breakpoint,
    // unless the user chose a state of their own. It runs again at the mobile breakpoint too, so a window that
    // widens out of the drawer gets the stored choice back. A layout effect, so a window between the two
    // breakpoints never paints the rail before the sidebar.
    useLayoutEffect(() => {
        dispatch(openDrawer({ drawerOpen: readSidebarExpanded() ?? opensExpanded }));
    }, [dispatch, opensExpanded, isMobile]);

    // Under 768px the sidebar is an off-canvas drawer: it goes away on every navigation, and when the
    // window narrows past the breakpoint.
    useEffect(() => {
        if (isMobile) {
            dispatch(openDrawer({ drawerOpen: false }));
        }
    }, [dispatch, isMobile, pathname]);

    // `overflow-x-clip`: an open temporary drawer would otherwise overflow the row. Clip, not hidden: a hidden row
    // is still a scroll container, and the Inbox's tab strip scrolling its active tab into view while the drawer
    // slides in would scroll the whole row, sidebar included, to the left. During the guided tour the app shows
    // but takes no input: only the tour's card and banner, outside it, do. The AI panel docked to the side narrows
    // the row by its width.
    return (
        <LazyMotion features={motionFeatures} strict>
            {/* Under reduced motion every `m` element below drops its transform, size and layout animation and keeps
            its opacity: a hand branch on `useReducedMotion` is left only for what this does not reach. */}
            <MotionConfig reducedMotion="user">
                <CreateDialogHost>
                    <div
                        inert={touring}
                        className={cn(
                            'flex h-full w-[calc(100%-var(--ai-side-dock-width,0px))] overflow-x-clip',
                            focusedEditMode ? 'bg-grey-200' : 'bg-background'
                        )}
                    >
                        <PageTitle
                            title={intl.formatMessage({ id: 'default-page-title' })}
                            showBreadcrumb
                            breadcrumbContent=""
                        />
                        <PanelBoxProperties />
                        {/* Skip to content, held above the window's top edge until focused. A button that focuses `<main>`
                    and not a link to its id: the hash would stay in the address, and a record's tabs carry it into
                    every navigation after. */}
                        <button
                            type="button"
                            onClick={() => mainRef.current?.focus()}
                            className={cn(
                                'bg-card text-foreground focus:shadow-popover text-body1 hit-area fixed start-2 top-2 z-(--z-tooltip) -translate-y-16 cursor-pointer rounded-md px-3 py-2 font-medium focus:translate-y-0',
                                FOCUS_RING
                            )}
                        >
                            {intl.formatMessage({ id: 'skip-to-content' })}
                        </button>
                        {!isFullscreen && <Sidebar />}
                        {!isFullscreen && <DetachedSubDrawer />}
                        {/* `min-w-0`: the page's own min-content width must not push the row past the viewport. While the tour
                    runs its banner sits above the top bar, and publishes the height it takes there. Focusable by script
                    only, for Skip to content and a page change, and with no ring of its own: it is the whole page. */}
                        <main
                            ref={mainRef}
                            tabIndex={-1}
                            className={cn(
                                'flex h-full min-w-0 flex-1 flex-col outline-none',
                                touring && 'pt-(--tour-banner-h)'
                            )}
                        >
                            <TopBar focusedMode={isFullscreen} />
                            <m.div
                                id={MAIN_CONTENT_AREA_ID}
                                // Its scroll is measured with the layout animations inside it: a scroll between Motion's
                                // two measures of a change, as a focus that brings a moved widget into sight, moves no cell.
                                layoutScroll
                                // `scroll-smooth` is mostly for the form section menu; reduced motion jumps instead.
                                className="relative flex h-full flex-col overflow-y-auto motion-safe:scroll-smooth"
                            >
                                {/* Wraps the routed content too: the AI chat session must outlive the `/ai-chat`
                            route so a response keeps streaming after the user follows a link out of it. */}
                                <AiChatSessionProvider>
                                    {!isFullscreen && (
                                        <FloatingAiAssistant showLauncher={false} prefersSideDock={isTimecardForm} />
                                    )}
                                    <OnboardingAssistant />
                                    <ListHeaderSlotsProvider>
                                        {/* Remounted as the tour starts and ends: the tour resets the cache, and a lazy query
                                    (the Home lists) fetches on mount only, so a page left mounted would stay empty. */}
                                        <Outlet key={touring ? 'tour' : 'app'} />
                                    </ListHeaderSlotsProvider>
                                </AiChatSessionProvider>
                            </m.div>
                        </main>
                        {!isFullscreen && <DetachedTemporaryDrawer />}
                        {!isFullscreen && <Inbox />}
                        {!isFullscreen && <GlobalSearchDialog />}
                        <LinkPeekHost />
                        <ExportRibbon />
                    </div>
                </CreateDialogHost>
            </MotionConfig>
        </LazyMotion>
    );
}

export default NewDesignLayout;
